import { FC, useEffect, useRef } from 'react'
import { styled } from '@mui/material/styles'

// Use require to bypass baseUrl resolution issue
// eslint-disable-next-line @typescript-eslint/no-var-requires
const vegaEmbed = require('vega-embed').default
// eslint-disable-next-line @typescript-eslint/no-var-requires
const vegaInterpreter = require('vega-interpreter').expressionInterpreter

interface VegaLiteChartProps {
  chartSpec: string
}

// Nexa AI color palette for charts
const CHART_COLORS = [
  '#144899', // Threat Detection - dark blue
  '#2c6ba2', // Vulnerability Management - blue
  '#066a5d', // Training & Awareness - teal
  '#216127', // Cloud Security - green
  '#75496a', // Identity & Access Management - purple
  '#8e9aff', // Additional - light purple
  '#c77eda', // Additional - magenta
  '#ffde6a', // Additional - yellow
  '#ff7f6b', // Additional - coral
  '#99fdc0' // Additional - mint
]

// Bar chart specific colors (stacked order from bottom to top)
const BAR_CHART_COLORS = ['#8e9aff', '#c77eda', '#ffde6a', '#ff7f6b', '#99fdc0']

// Line/Area chart colors
const LINE_AREA_COLOR = '#75496a' // Purple fill
const LINE_STROKE_COLOR = '#c77eda' // Pink/magenta line

const ChartWrapper = styled('div')({
  marginTop: '16px',
  padding: '16px',
  backgroundColor: 'transparent',
  borderRadius: '8px',
  overflow: 'auto',
  '& .vega-embed': {
    width: '100%'
  },
  '& .vega-embed summary': {
    display: 'none'
  }
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getChartType = (spec: any): 'pie' | 'bar' | 'line' | 'area' | 'unknown' => {
  const mark = spec.mark
  if (typeof mark === 'string') {
    if (mark === 'arc') return 'pie'
    if (mark === 'bar') return 'bar'
    if (mark === 'line') return 'line'
    if (mark === 'area') return 'area'
  } else if (typeof mark === 'object' && mark.type) {
    if (mark.type === 'arc') return 'pie'
    if (mark.type === 'bar') return 'bar'
    if (mark.type === 'line') return 'line'
    if (mark.type === 'area') return 'area'
  }
  return 'unknown'
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const applyNexaTheme = (spec: any): any => {
  const chartType = getChartType(spec)

  // Base config for all charts - our theme takes precedence
  const nexaConfig = {
    background: 'transparent',
    font: 'Quicksand, sans-serif',
    title: {
      color: '#e9e0e4',
      fontSize: 14,
      fontWeight: 600
    },
    axis: {
      labelColor: '#d0c3cc',
      titleColor: '#d0c3cc',
      gridColor: '#6c5b67',
      gridDash: [4, 4],
      domainColor: 'transparent',
      tickColor: 'transparent',
      labelFontSize: 12,
      titleFontSize: 14
    },
    axisX: {
      labelColor: '#d0c3cc',
      titleColor: '#d0c3cc',
      gridColor: 'transparent',
      domainColor: 'transparent',
      tickColor: 'transparent'
    },
    axisY: {
      labelColor: '#d0c3cc',
      titleColor: '#d0c3cc',
      gridColor: '#6c5b67',
      gridDash: [4, 4],
      domainColor: 'transparent',
      tickColor: 'transparent'
    },
    legend: {
      labelColor: '#e9e0e4',
      titleColor: '#e9e0e4',
      labelFontSize: 14,
      symbolSize: 144,
      symbolType: 'square'
    },
    view: {
      stroke: 'transparent'
    },
    range: {
      category: CHART_COLORS
    }
  }

  // Create a new spec with our modifications
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let themedSpec: any = {
    ...spec,
    background: 'transparent',
    config: nexaConfig
  }

  // Chart type specific modifications
  switch (chartType) {
    case 'pie':
      themedSpec.mark = {
        type: 'arc',
        innerRadius: 60,
        cornerRadius: 6,
        stroke: '#1e1a1d',
        strokeWidth: 2,
        padAngle: 0.03
      }
      themedSpec.config = {
        ...nexaConfig,
        legend: {
          ...nexaConfig.legend,
          orient: 'right',
          direction: 'vertical',
          offset: 48,
          legendY: -20,
          titleAnchor: 'start',
          titleOrient: 'top',
          titlePadding: 0,
          padding: 0,
          rowPadding: 12
        }
      }
      break

    case 'bar': {
      // Check if there's a color encoding - if not, use default bar color
      const hasColorEncoding = spec.encoding?.color !== undefined
      themedSpec.mark = {
        type: 'bar',
        cornerRadiusTopLeft: 4,
        cornerRadiusTopRight: 4,
        cornerRadiusBottomLeft: 4,
        cornerRadiusBottomRight: 4,
        // Apply default color if no color encoding
        ...(hasColorEncoding ? {} : { color: BAR_CHART_COLORS[0] })
      }
      themedSpec.config = {
        ...nexaConfig,
        range: {
          category: BAR_CHART_COLORS
        }
      }
      break
    }

    case 'line':
      // Convert line chart to area chart with line on top (layered)
      themedSpec = {
        ...spec,
        background: 'transparent',
        config: nexaConfig,
        layer: [
          {
            mark: {
              type: 'area',
              color: LINE_AREA_COLOR,
              opacity: 0.4,
              line: false
            },
            encoding: spec.encoding
          },
          {
            mark: {
              type: 'line',
              color: LINE_STROKE_COLOR,
              strokeWidth: 2
            },
            encoding: spec.encoding
          }
        ]
      }
      // Remove top-level mark and encoding since we're using layers
      delete themedSpec.mark
      delete themedSpec.encoding
      break

    case 'area':
      themedSpec.mark = {
        type: 'area',
        color: LINE_AREA_COLOR,
        opacity: 0.4,
        line: {
          color: LINE_STROKE_COLOR,
          strokeWidth: 2
        }
      }
      break
  }

  return themedSpec
}

const VegaLiteChart: FC<VegaLiteChartProps> = ({ chartSpec }) => {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!containerRef.current) return

    let view: { finalize: () => void } | null = null

    const renderChart = async () => {
      try {
        const parsed = JSON.parse(chartSpec)
        const themedSpec = applyNexaTheme(parsed)

        const result = await vegaEmbed(containerRef.current, themedSpec, {
          actions: false,
          renderer: 'svg',
          tooltip: {
            theme: 'dark'
          },
          ast: true,
          expr: vegaInterpreter
        })
        view = result.view
      } catch (e) {
        console.error('Failed to render chart:', e)
        if (containerRef.current) {
          containerRef.current.innerHTML = '<span style="color: #ff6b6b;">Failed to render chart</span>'
        }
      }
    }

    renderChart()

    // Cleanup
    return () => {
      if (view) {
        view.finalize()
      }
    }
  }, [chartSpec])

  return <ChartWrapper ref={containerRef} />
}

export default VegaLiteChart
